import React, { useState } from 'react';
import axios from 'axios';
import { biosdkOperationUrl, CONVERTER_SINGLE_API_BASE, logBiosdkUiRoute } from './config';
import { User, Upload, Loader2, AlertCircle, Clock, Gauge } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Label } from './components/ui/label';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';

function FaceQualityChecker() {
  const [file, setFile] = useState(null);
  const [injpegImageUrl, setInJpegImageUrl] = useState(null);
  const [timing, setTiming] = useState({ convertInput: 0, convertIso: 0, Quality: 0 });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [jp2isoImageUrl, setjp2IsoImageUrl] = useState(null);

  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const normalizeAnalyticsInfo = (raw) => {
    if (raw == null) return {};
    if (typeof raw === 'string') {
      try {
        return normalizeAnalyticsInfo(JSON.parse(raw));
      } catch {
        return {};
      }
    }
    if (Array.isArray(raw)) {
      const isDeepFaceFaceBlock = (item) =>
        item &&
        typeof item === 'object' &&
        (item.dominant_gender != null ||
          item.dominant_emotion != null ||
          item.dominant_race != null ||
          item.age != null ||
          item.face_confidence != null);

      if (raw.some(isDeepFaceFaceBlock)) {
        const conf = (o) => {
          const n = Number(o?.face_confidence ?? o?.faceConfidence);
          return Number.isNaN(n) ? 0 : n;
        };
        let best = null;
        let bestC = -1;
        for (const item of raw) {
          if (isDeepFaceFaceBlock(item)) {
            const c = conf(item);
            if (c > bestC) {
              bestC = c;
              best = item;
            }
          }
        }
        return best && typeof best === 'object' ? { ...best } : {};
      }

      const out = {};
      for (const item of raw) {
        if (item && typeof item === 'object') {
          const key = item.key ?? item.name ?? item.k;
          const val = item.value ?? item.val ?? item.v;
          if (key != null) out[String(key)] = val;
        }
      }
      return out;
    }
    if (typeof raw === 'object') return raw;
    return {};
  };

  const pickValue = (obj, ...keys) => {
    if (!obj || typeof obj !== 'object') return undefined;
    for (const k of keys) {
      if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
    }
    const lowerMap = Object.fromEntries(Object.entries(obj).map(([k, v]) => [String(k).toLowerCase(), v]));
    for (const k of keys) {
      const v = lowerMap[String(k).toLowerCase()];
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return undefined;
  };

  const pickDominantFromDistribution = (obj, ...keys) => {
    const raw = pickValue(obj, ...keys);
    if (!raw) return undefined;
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object') {
      let bestKey;
      let bestVal = -Infinity;
      for (const [k, v] of Object.entries(raw)) {
        const num = Number(v);
        if (!Number.isNaN(num) && num > bestVal) {
          bestVal = num;
          bestKey = k;
        }
      }
      return bestKey;
    }
    return String(raw);
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setInJpegImageUrl(null); 
    setSubmitted(false);
    setShowAdvanced(false);
    setError(null); 
    setExtractedData(null); 

    if (!selectedFile) {
      setError('No file selected.');
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    let startConvertInput = performance.now();
    let convertInputDuration = 0;

    try {
      if (['jpeg', 'jpg', 'bmp', 'png'].includes(fileExtension)) {
        setInJpegImageUrl(URL.createObjectURL(selectedFile));
      } else if (fileExtension === 'iso') {
        const isoFormData = new FormData();
        isoFormData.append('file', selectedFile);
        const response = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });
        const jpegBlob = new Blob([response.data], { type: 'image/jpeg' });
        setInJpegImageUrl(URL.createObjectURL(jpegBlob));
        setjp2IsoImageUrl(null);
      } else if (fileExtension === 'jp2') {
        setError('JP2 is not supported in direct mode. Use jpg/jpeg/png/bmp/iso.');
        setFile(null);
        return;
      } else {
        setError('Unsupported file type.');
        setFile(null);
        return;
      }
      
      setTiming((prevTiming) => ({
        ...prevTiming,
        convertInput: convertInputDuration,
      }));

    } catch (err) {
      setError('An error occurred while processing the file.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setIsProcessing(true); 

    if (!file) {
      setError('Please upload a file.');
      setIsProcessing(false); 
      return;
    }

    setShowAdvanced(false);
    setError(null);

    const fileExtension = file.name.split('.').pop().toLowerCase();
    let facebdbPlaceholder = [];
    let startConvertInput = performance.now();
    let convertInputDuration = 0;

    try {
    if (fileExtension === 'jpeg' || fileExtension === 'jpg'|| fileExtension === 'bmp'|| fileExtension === 'png' || fileExtension === 'iso') {
        const isoArrayBuffer = await file.arrayBuffer();
        const endConvertInput = performance.now();
        convertInputDuration = endConvertInput - startConvertInput;
        facebdbPlaceholder = JSON.stringify(Array.from(new Uint8Array(isoArrayBuffer)));
      } else {
        throw new Error('Unsupported file type. Use jpg/jpeg/png/bmp/iso.');
      }
   
      const templateResponse = await fetch('/face-quality-request.json');
      if (!templateResponse.ok) throw new Error(`Template fetch failed with status: ${templateResponse.status}`);

      const templateText = await templateResponse.text();
      const updatedTemplate = templateText.replace('facebdb', facebdbPlaceholder);

      const versionRequestTemplateResponse = await fetch('/extract_request-template.json');
      if (!versionRequestTemplateResponse.ok) throw new Error(`Version request template fetch failed with status: ${versionRequestTemplateResponse.status}`);

      const versionRequestTemplateText = await versionRequestTemplateResponse.text();
      const base64EncodedExtract = arrayBufferToBase64(new TextEncoder().encode(updatedTemplate));
      const finalRequestBodyExtract = JSON.parse(versionRequestTemplateText.replace('encoded_request', base64EncodedExtract));
      finalRequestBodyExtract.operation = 'check-quality';
      
      const isRawInput = fileExtension !== 'iso';
      const qualityUrl = biosdkOperationUrl('process', isRawInput);
      logBiosdkUiRoute('FaceQuality', isRawInput ? 'rawRoute' : 'isoRoute', qualityUrl, {
        operation: 'check-quality',
      });
      const extractResponse = await axios.post(qualityUrl, finalRequestBodyExtract, {
        headers: { 'Content-Type': 'application/json' },
      });

      const inner = extractResponse.data?.response?.response;
      const scores = inner?.scores || {};
      const extractResult =
        scores.FACE ||
        scores.Face ||
        scores.face ||
        scores[Object.keys(scores)[0]];
      if (!extractResult) {
        const errMsg = inner?.statusMessage || extractResponse.data?.errors?.[0]?.message;
        throw new Error(
          errMsg ||
            'Quality check did not return face scores (is DeepFace/Python running and biosdk URL set to …/biosdk-service?)'
        );
      }

      const score = Number(extractResult.score ?? 0);
      const analyticsInfo = normalizeAnalyticsInfo(extractResult.analyticsInfo);

      const age = pickValue(analyticsInfo, 'age', 'Age');
      const dominant_gender =
        pickValue(analyticsInfo, 'dominant_gender', 'dominantGender') ||
        pickDominantFromDistribution(analyticsInfo, 'gender');
      const dominant_emotion =
        pickValue(analyticsInfo, 'dominant_emotion', 'dominantEmotion') ||
        pickDominantFromDistribution(analyticsInfo, 'emotion');
      const dominant_race =
        pickValue(analyticsInfo, 'dominant_race', 'dominantRace') ||
        pickDominantFromDistribution(analyticsInfo, 'race');
      const face_confidence = pickValue(analyticsInfo, 'face_confidence', 'faceConfidence', 'confidence');

      setShowAdvanced(true);
      setError(null);

      setTiming({
        convertInput: timing.convertInput,
        convertIso: convertInputDuration,
        Quality: 0,
      });
      setExtractedData({
        score,
        emotion: pickValue(analyticsInfo, 'emotion'),
        dominant_emotion,
        gender: pickValue(analyticsInfo, 'gender'),
        dominant_gender,
        race: pickValue(analyticsInfo, 'race'),
        dominant_race,
        region: pickValue(analyticsInfo, 'region'),
        age,
        face_confidence,
      });

    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during the extraction process';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setInJpegImageUrl(null);
    setExtractedData(null);
    setError(null);
    setSubmitted(false);
    setShowAdvanced(false);
  };

  const getQualityLevel = (score) => {
    if (score >= 80) return { level: 'Excellent', color: 'text-green-600', bg: 'bg-green-500' };
    if (score >= 60) return { level: 'Good', color: 'text-blue-600', bg: 'bg-blue-500' };
    if (score >= 40) return { level: 'Fair', color: 'text-yellow-600', bg: 'bg-yellow-500' };
    return { level: 'Poor', color: 'text-red-600', bg: 'bg-red-500' };
  };

  return (
    <div className="biochq-page">
      <div className="biochq-container">
        <Card className="biochq-card face">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Gauge className="h-8 w-8 text-face" />
              <CardTitle className="text-3xl font-bold text-gray-800">Face Quality Checker</CardTitle>
            </div>
            <CardDescription className="text-gray-600">
              Analyze face image quality for biometric enrollment and verification
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Input Section */}
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="file" className="text-gray-700 font-medium">
                    Upload Face Image
                  </Label>
                  <div className="upload-zone">
                    <input
                      type="file"
                      id="file"
                      accept=".jpeg,.jpg,.iso,.jp2,.png,.bmp"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label htmlFor="file" className="cursor-pointer">
                      <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG, BMP, JP2, or ISO</p>
                    </label>
                  </div>
                </div>

                {file && injpegImageUrl && (
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-medium">Preview</Label>
                    <div className="preview-container">
                      <img src={injpegImageUrl} alt="Uploaded face" className="preview-image" />
                      <Badge variant="face" className="absolute top-2 right-2">Face</Badge>
                    </div>
                    <p className="text-xs text-gray-500 text-center">{file.name}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    onClick={handleSubmit}
                    disabled={!file || isProcessing}
                    variant="face"
                    className="flex-1"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Gauge className="mr-2 h-4 w-4" />
                        Check Quality
                      </>
                    )}
                  </Button>
                  <Button onClick={handleReset} variant="outline" disabled={isProcessing}>
                    Reset
                  </Button>
                </div>
              </div>

              {/* Output Section */}
              <div className="space-y-6">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {extractedData && !error && (
                  <>
                    {/* Overall Score */}
                    {(() => {
                      const qualityLevel = getQualityLevel(extractedData.score);
                      return (
                        <Card className={`border-2 ${
                          extractedData.score >= 80 ? 'border-green-300 bg-green-50' :
                          extractedData.score >= 60 ? 'border-blue-300 bg-blue-50' :
                          extractedData.score >= 40 ? 'border-yellow-300 bg-yellow-50' : 'border-red-300 bg-red-50'
                        }`}>
                          <CardContent className="pt-6 text-center">
                            <div className="relative inline-flex items-center justify-center">
                              <svg className="w-32 h-32 transform -rotate-90">
                                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="none" className="text-gray-200" />
                                <circle
                                  cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="none"
                                  strokeDasharray={`${extractedData.score * 3.52} 352`}
                                  style={{ stroke: qualityLevel.bg.includes('green') ? '#22c55e' : 
                                           qualityLevel.bg.includes('blue') ? '#3b82f6' :
                                           qualityLevel.bg.includes('yellow') ? '#eab308' : '#ef4444' }}
                                />
                              </svg>
                              <div className="absolute">
                                <p className={`text-3xl font-bold ${qualityLevel.color}`}>{extractedData.score}</p>
                                <p className="text-xs text-gray-500">out of 100</p>
                              </div>
                            </div>
                            <Badge className={`mt-4 ${qualityLevel.bg} text-white`}>{qualityLevel.level} Quality</Badge>
                          </CardContent>
                        </Card>
                      );
                    })()}

                    {/* Face Analysis Details */}
                    <Card className="bg-gray-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Face Analysis</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Age:</span>
                          <span className="font-medium">{extractedData.age ?? 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Gender:</span>
                          <Badge variant="outline">{extractedData.dominant_gender ?? 'N/A'}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Emotion:</span>
                          <Badge variant="outline">{extractedData.dominant_emotion ?? 'N/A'}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Race:</span>
                          <Badge variant="outline">{extractedData.dominant_race ?? 'N/A'}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Face Confidence:</span>
                          <span className="font-medium">{extractedData.face_confidence ?? 'N/A'}</span>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Timing */}
                    {showAdvanced && (
                      <Card className="timing-card">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Timing Details
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-2 gap-4 text-sm text-center">
                          <div>
                            <p className="text-gray-600">Display Conversion</p>
                            <p className="font-medium">{timing.convertInput.toFixed(2)} ms</p>
                          </div>
                          <div>
                            <p className="text-gray-600">ISO Conversion</p>
                            <p className="font-medium">{timing.convertIso.toFixed(2)} ms</p>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}

                {!extractedData && !error && submitted && (
                  <div className="flex items-center justify-center h-48 text-gray-400">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                )}

                {!extractedData && !error && !submitted && (
                  <div className="empty-state">
                    <User className="h-12 w-12 mb-2 opacity-50" />
                    <p className="text-sm">Upload a face image to check quality</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default FaceQualityChecker;
